/**
 * PRODUCTION EMAIL ENCRYPTION UTILITY
 * 
 * Secure AES-GCM encryption for email storage in production.
 * 
 * SECURITY: Encryption/decryption is now handled server-side via Cloud Functions
 * to keep the encryption key secure. The key is never exposed in the frontend bundle.
 * 
 * Cloud Functions used:
 * - encryptEmail: Server-side email encryption
 * - decryptEmail: Server-side email decryption
 * - verifyEmail: Server-side email verification/decryption
 */

import { getCloudFunction } from './cloudFunctions';
import { isValidEmail as validateEmail } from './formValidation';

/**
 * Check if an email is valid
 * @deprecated Use isValidEmail from '../utilities/formValidation' instead
 * This is kept for backward compatibility
 */
export function isValidEmail(email: string): boolean {
  return validateEmail(email);
}

// Get Cloud Functions reference
function getEmailEncryptionFunctions() {
  return {
    encryptEmail: getCloudFunction('encryptEmail'),
    decryptEmail: getCloudFunction('decryptEmail'),
    verifyEmail: getCloudFunction('verifyEmail'),
    batchVerifyEmails: getCloudFunction('batchVerifyEmails'),
  };
}

/**
 * Try to decode a legacy base64-encoded email locally (no Cloud Function call)
 */
function tryLocalBase64Decode(encodedEmail: string): string | null {
  try {
    const decoded = atob(encodedEmail);
    if (isValidEmail(decoded)) {
      return decoded;
    }
  } catch {
    // Not valid base64
  }
  return null;
}

/**
 * Encrypt an email address for secure storage using AES encryption
 * Now uses Cloud Functions to keep the encryption key secure server-side
 */
export async function encryptEmail(email: string): Promise<string> {
  if (!email) return '';
  
  try {
    const functions = getEmailEncryptionFunctions();
    const result = await functions.encryptEmail({ email });
    
    const response = result.data as { success: boolean; encryptedEmail: string };
    if (response.success && response.encryptedEmail) {
      return response.encryptedEmail;
    }
    
    throw new Error('Encryption failed: Invalid response from server');
  } catch (error) {
    console.error('Email encryption failed:', error);
    throw new Error('Failed to encrypt email');
  }
}

/**
 * Synchronous email encryption using base64 (for immediate compatibility)
 * Use encryptEmail() for better security when possible
 */
export function hashEmail(email: string): string {
  if (!email) return '';
  
  try {
    return btoa(email.toLowerCase());
  } catch (error) {
    console.error('Failed to encode email:', error);
    throw new Error('Failed to encode email');
  }
}

/**
 * Decrypt an AES-encrypted email
 * Now uses Cloud Functions to keep the encryption key secure server-side
 */
async function decryptEmail(encryptedData: string): Promise<string> {
  try {
    const functions = getEmailEncryptionFunctions();
    const result = await functions.decryptEmail({ encryptedEmail: encryptedData });
    
    const response = result.data as { success: boolean; email: string };
    if (response.success && response.email) {
      return response.email;
    }
    
    throw new Error('Decryption failed: Invalid response from server');
  } catch (error) {
    console.warn('Failed to decrypt email:', error);
    throw error;
  }
}

/**
 * Decrypt an email address from storage
 */
export function verifyEmail(encodedEmail: string): string {
  const trimmed = encodedEmail.trim();
  if (!trimmed) return '';
  
  if (isValidEmail(trimmed)) {
    return trimmed;
  }
  
  const decoded = tryLocalBase64Decode(trimmed);
  if (decoded) {
    return decoded;
  }
  
  throw new Error('Email data is corrupted or invalid');
}

/**
 * Async email verification for AES encrypted emails
 * Now uses Cloud Functions to keep the encryption key secure server-side
 */
export async function verifyEmailAsync(encodedEmail: string): Promise<string> {
  const trimmed = encodedEmail.trim();
  if (!trimmed) return '';
  
  if (isValidEmail(trimmed)) {
    return trimmed;
  }
  
  const localDecoded = tryLocalBase64Decode(trimmed);
  if (localDecoded) {
    return localDecoded;
  }
  
  try {
    const functions = getEmailEncryptionFunctions();
    const result = await functions.verifyEmail({ encodedEmail: trimmed });
    
    const response = result.data as { success: boolean; email: string | null };
    if (response.success && response.email) {
      return response.email;
    }
  } catch {
    // Cloud Function unavailable or returned an error — try local decode as fallback
    try {
      return verifyEmail(trimmed);
    } catch {
      // Expected for AES-encrypted emails when Cloud Function is unavailable
    }
  }
  
  throw new Error('Failed to verify email');
}

/**
 * Batch verify/decrypt multiple emails efficiently via Cloud Function
 */
export async function batchVerifyEmails(
  items: Array<{ id: string; encodedEmail: string }>
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (items.length === 0) return result;

  const needsCloud: Array<{ id: string; encodedEmail: string }> = [];

  for (const item of items) {
    const trimmed = item.encodedEmail.trim();
    if (!trimmed) continue;

    if (isValidEmail(trimmed)) {
      result.set(item.id, trimmed);
      continue;
    }

    const localDecoded = tryLocalBase64Decode(trimmed);
    if (localDecoded) {
      result.set(item.id, localDecoded);
      continue;
    }

    needsCloud.push({ id: item.id, encodedEmail: trimmed });
  }

  if (needsCloud.length === 0) return result;

  const BATCH_SIZE = 100;
  const functions = getEmailEncryptionFunctions();

  for (let i = 0; i < needsCloud.length; i += BATCH_SIZE) {
    const batch = needsCloud.slice(i, i + BATCH_SIZE);
    try {
      const response = await functions.batchVerifyEmails({ items: batch });
      const data = response.data as { success: boolean; emails: Record<string, string> };
      if (data.success && data.emails) {
        for (const [id, email] of Object.entries(data.emails)) {
          if (email) result.set(id, email);
        }
      }
    } catch {
      await Promise.all(
        batch.map(async (item) => {
          try {
            const email = await verifyEmailAsync(item.encodedEmail);
            result.set(item.id, email);
          } catch {
            // Skip undecryptable emails
          }
        })
      );
    }
  }

  return result;
}

/**
 * Get a display version of an email (for UI purposes)
 */
export function getEmailDisplay(email: string): string {
  if (!email) return '';
  
  // If it's already a plain email, return it
  if (isValidEmail(email)) {
    return email;
  }
  
  try {
    return verifyEmail(email);
  } catch (error) {
    console.error('Error displaying email:', error);
    return '[Email Display Error]';
  }
}

/**
 * Async version for better email display with AES support
 */
export async function getEmailDisplayAsync(email: string): Promise<string | null> {
  if (!email) return null;
  
  if (isValidEmail(email)) {
    return email;
  }
  
  try {
    return await verifyEmailAsync(email);
  } catch {
    return null;
  }
}